/**
 * @file MediaDropZone.tsx
 *
 * Drag-and-drop + click-to-browse upload target, shared by the product form's
 * image gallery and downloadable-file sections. Presentational and controlled:
 * it validates each file against the type/size/slot rules and reports accepted
 * files via `onFilesSelected` and rejected reasons via `onError` (as stable
 * codes the parent localizes). In image mode it restricts to image MIME types;
 * `acceptAllTypes` lifts that for downloadable files.
 */

import { useRef, useState } from 'react';
import type { DragEvent } from 'react';
import { Tile } from '@group.one/react-gravity';
import { ACCEPTED_IMAGE_TYPES } from '../products/form/product-form-constants';
import styles from './MediaDropZone.module.css';

/** Why one or more files were rejected, for the parent to localize. */
export type MediaErrorCode = 'type' | 'size' | 'limit';

interface MediaDropZoneProps {
  onFilesSelected: (files: File[]) => void;
  onError?: (codes: MediaErrorCode[]) => void;
  /** Lift the image-type restriction (used for downloadable files). */
  acceptAllTypes?: boolean;
  maxFileSizeMb: number;
  /** How many more files may be added before the cap is hit. */
  remainingSlots: number;
  disabled?: boolean;
  /** Localized prompt shown in the drop zone. */
  title: string;
  /** Localized label for the browse button. */
  browseLabel: string;
  /** Localized hint (accepted types / size) shown below the drop zone. */
  hint?: string;
  /** Opens the media library; when set, a "select from library" link is shown. */
  onSelectFromLibrary?: () => void;
  /** Localized label for the library link (required when `onSelectFromLibrary` is set). */
  libraryLabel?: string;
  /** Localized "or" separator between the two actions. */
  orLabel?: string;
  /** Show the file-type tile icon above the prompt (default true). */
  showTile?: boolean;
  /** `data-testid` namespace for this drop zone; defaults to `media-dropzone`. */
  testId?: string;
}

/**
 * Renders a drop zone that validates and forwards selected files.
 *
 * @param props - Handlers, limits, localized copy, and the disabled flag.
 *
 * @returns The drop-zone element.
 */
export function MediaDropZone({
  onFilesSelected,
  onError,
  acceptAllTypes = false,
  maxFileSizeMb,
  remainingSlots,
  disabled = false,
  title,
  browseLabel,
  hint,
  onSelectFromLibrary,
  libraryLabel,
  orLabel,
  showTile = true,
  testId = 'media-dropzone',
}: MediaDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFiles = (fileList: FileList | null): void => {
    if (!fileList || fileList.length === 0) {
      return;
    }
    const maxBytes = maxFileSizeMb * 1024 * 1024;
    const codes = new Set<MediaErrorCode>();
    const valid: File[] = [];
    for (const file of Array.from(fileList)) {
      if (!acceptAllTypes && !ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        codes.add('type');
        continue;
      }
      if (file.size > maxBytes) {
        codes.add('size');
        continue;
      }
      valid.push(file);
    }
    let accepted = valid;
    if (valid.length > remainingSlots) {
      codes.add('limit');
      accepted = valid.slice(0, Math.max(0, remainingSlots));
    }
    if (codes.size > 0) {
      onError?.(Array.from(codes));
    }
    if (accepted.length > 0) {
      onFilesSelected(accepted);
    }
  };

  const onDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setDragActive(false);
    if (disabled) {
      return;
    }
    handleFiles(event.dataTransfer.files);
  };

  const onDragOver = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    if (!disabled) {
      setDragActive(true);
    }
  };

  return (
    <div className="gv-flex gv-flex-col gv-gap-sm">
      <div
        className={`${styles.dropZone} ${dragActive ? styles.dragActive : ''} gv-flex gv-flex-col gv-items-center gv-justify-center gv-gap-md gv-p-lg gv-radius`}
        data-testid={testId}
        data-disabled={disabled || undefined}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={() => setDragActive(false)}
      >
        {showTile && (
          <div className={styles.tileIcon} aria-hidden="true">
            <Tile name="file-type-image" type="duotone" size="sm" />
          </div>
        )}
        <p className="gv-text-sm gv-text-bold">{title}</p>
        <div className={styles.actions}>
          <span
            role="button"
            tabIndex={disabled ? -1 : 0}
            className={`gv-text-sm gv-text-bold ${styles.actionLink}`}
            data-testid={`${testId}-browse`}
            aria-disabled={disabled || undefined}
            onClick={() => !disabled && inputRef.current?.click()}
          >
            {browseLabel}
          </span>
          {onSelectFromLibrary && (
            <>
              <span className={`gv-text-sm ${styles.actionPlaceholder}`}>{orLabel}</span>
              <span
                role="button"
                tabIndex={disabled ? -1 : 0}
                className={`gv-text-sm gv-text-bold ${styles.actionLink}`}
                data-testid={`${testId}-library`}
                aria-disabled={disabled || undefined}
                onClick={() => !disabled && onSelectFromLibrary()}
              >
                {libraryLabel}
              </span>
            </>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          className={styles.input}
          data-testid={`${testId}-input`}
          accept={acceptAllTypes ? undefined : ACCEPTED_IMAGE_TYPES.join(',')}
          aria-label={browseLabel}
          disabled={disabled}
          onChange={(event) => {
            handleFiles(event.target.files);
            event.target.value = '';
          }}
        />
      </div>
      {hint && <p className={`gv-caption-lg ${styles.hint}`}>{hint}</p>}
    </div>
  );
}
